import { useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";

interface FAQ {
  question: string;
  answer: string;
  isVoiceSearch?: boolean;
}

interface FAQSectionProps {
  tagline: string;
  title: string;
  subtitle: string;
  faqs: FAQ[];
}

export default function FAQSection({ tagline, title, subtitle, faqs }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const headRef = useScrollReveal();
  const listRef = useScrollReveal();

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="bg-white py-32 px-6 lg:px-14">
      <div className="max-w-4xl mx-auto">
        <div ref={headRef} className="sr-hidden text-center mb-14">
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="gold-rule" />
            <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.22em]">{tagline}</p>
            <span className="gold-rule" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-charcoal font-bold mb-4">
            {title}
          </h2>
          <p className="text-stone-400 font-sans text-base leading-relaxed max-w-lg mx-auto">{subtitle}</p>
        </div>

        <div ref={listRef} className="sr-hidden sr-delay-1 flex flex-col gap-2.5">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                openIndex === i
                  ? "border-gold/50 bg-cream shadow-sm"
                  : "border-stone-100 bg-white hover:border-stone-200"
              }`}
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-start justify-between px-7 py-5 text-left cursor-pointer gap-4"
                aria-expanded={openIndex === i}
              >
                <div className="flex items-start gap-3 flex-1">
                  {faq.isVoiceSearch && (
                    <div className="w-5 h-5 flex items-center justify-center text-gold flex-shrink-0 mt-0.5">
                      <i className="ri-mic-line text-xs"></i>
                    </div>
                  )}
                  <span
                    className={`font-sans font-semibold text-sm leading-snug transition-colors ${
                      openIndex === i ? "text-charcoal" : "text-stone-700"
                    }`}
                  >
                    {faq.question}
                  </span>
                </div>
                <div
                  className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                    openIndex === i ? "bg-gold text-white rotate-45" : "bg-stone-100 text-stone-400"
                  }`}
                >
                  <i className="ri-add-line text-sm"></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-7 pb-7">
                  {!faq.isVoiceSearch && <div className="w-8 h-px bg-gold mb-4" />}
                  {faq.isVoiceSearch && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-4 h-4 flex items-center justify-center text-gold">
                        <i className="ri-mic-fill text-xs"></i>
                      </div>
                      <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-gold">Voice Search Answer</span>
                    </div>
                  )}
                  <p className="text-stone-500 font-sans text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
